// Self-contained map-camera controls for the FTTH design page.
//
// These are ported from AnnotationMap.jsx (the proven operator annotation map)
// so the design page gets the SAME rotate / tilt / Ctrl-camera behaviour
// without touching that 2400-line critical file. Everything here is local and
// dependency-free: no drawing-mode refs, no shared module constants - it works
// against any @vis.gl/react-google-maps VECTOR-rendered Map.
//
// Visual language matches AnnotationMap exactly: dark-glass HUD card, anchored
// bottom-start (inset-inline-start), RTL, Vazirmatn font.
//
// Rotation and tilt require a vector map; on a raster map id setHeading /
// setTilt are silent no-ops, which degrades safely to "the buttons do nothing".

import { useCallback, useEffect, useState } from 'react';

// Google Earth style oblique stops. 45 deg is the classic Earth view; 67.5 is
// the steepest the Maps JS vector renderer allows and shows facades clearest.
// Flat (0) is best for precise placement, so reset-to-north also clears it.
const TILT_STEPS = [0, 45, 67.5];

// Shared dark-glass card surface, anchored bottom-start. `bottom` lets callers
// stack two of these without collisions (rotate sits above the type toggle).
function glassCard(bottom) {
  return {
    position: 'absolute',
    bottom,
    insetInlineStart: 14,
    zIndex: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: 5,
    background:
      'linear-gradient(135deg, rgba(15,23,42,0.94) 0%, rgba(30,41,59,0.92) 100%)',
    borderRadius: 11,
    border: '1px solid rgba(148,163,184,0.22)',
    boxShadow:
      '0 1px 0 rgba(255,255,255,0.07) inset, 0 8px 24px rgba(2,6,23,0.55), 0 1px 3px rgba(0,0,0,0.45)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    direction: 'rtl',
  };
}

// View-rotation instrument: rotate CCW/CW (+/-30 deg), cycle the camera tilt
// through TILT_STEPS, and a reset-to-north button whose needle counter-rotates
// with the map so it doubles as a compass. Heading / tilt stay in sync with the
// live map via its `heading_changed` / `tilt_changed` listeners.
export function MapRotateControl({ map }) {
  const [heading, setHeading] = useState(0);
  const [tilt, setTilt] = useState(0);

  useEffect(() => {
    if (!map) return undefined;
    const sync = () => {
      setHeading(Math.round(map.getHeading?.() || 0));
      setTilt(Math.round(map.getTilt?.() || 0));
    };
    sync();
    const lh = map.addListener('heading_changed', sync);
    const lt = map.addListener('tilt_changed', sync);
    return () => {
      window.google.maps.event.removeListener(lh);
      window.google.maps.event.removeListener(lt);
    };
  }, [map]);

  const rotateBy = useCallback((delta) => {
    if (!map) return;
    const next = ((((map.getHeading?.() || 0) + delta) % 360) + 360) % 360;
    map.setHeading(next);
  }, [map]);

  const cycleTilt = useCallback(() => {
    if (!map) return;
    const cur = Math.round(map.getTilt?.() || 0);
    const idx = TILT_STEPS.findIndex((t) => Math.abs(t - cur) < 6);
    const next = TILT_STEPS[(idx + 1) % TILT_STEPS.length];
    map.setTilt(next);
  }, [map]);

  const resetNorth = useCallback(() => {
    if (!map) return;
    map.setHeading(0);
    map.setTilt(0);
  }, [map]);

  const sideBtn = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 34,
    height: 30,
    padding: 0,
    background: 'transparent',
    color: '#cbd5e1',
    border: '1px solid transparent',
    borderRadius: 7,
    cursor: 'pointer',
    transition: 'background 140ms ease, color 140ms ease',
  };
  const hoverIn = (e) => {
    e.currentTarget.style.background = 'rgba(51,65,85,0.7)';
    e.currentTarget.style.color = '#f1f5f9';
  };
  const hoverOut = (e) => {
    e.currentTarget.style.background = 'transparent';
    e.currentTarget.style.color = '#cbd5e1';
  };

  return (
    <div style={glassCard(60)}>
      <button
        type="button"
        aria-label="چرخش پادساعتگرد"
        title="چرخش پادساعتگرد"
        onClick={() => rotateBy(-30)}
        onMouseEnter={hoverIn}
        onMouseLeave={hoverOut}
        style={sideBtn}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 12a9 9 0 1 0 2.6-6.3" />
          <path d="M3 4v4.5h4.5" />
        </svg>
      </button>

      <button
        type="button"
        aria-label={`جهت فعلی ${heading} درجه؛ بازنشانی به شمال`}
        title={`جهت: ${heading}°  ·  کلیک برای بازنشانی به شمال  ·  میانبر: Ctrl را نگه دارید و بکشید تا نقشه بچرخد`}
        onClick={resetNorth}
        style={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          width: 44,
          height: 30,
          padding: 0,
          color: '#5eead4',
          background: 'rgba(45,212,191,0.10)',
          border: '1px solid rgba(45,212,191,0.28)',
          borderRadius: 7,
          cursor: 'pointer',
          transition: 'background 140ms ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(45,212,191,0.18)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(45,212,191,0.10)'; }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          aria-hidden="true"
          style={{ transform: `rotate(${-heading}deg)`, transition: 'transform 220ms ease' }}
        >
          <path d="M12 2 L7.5 14 L12 11.5 L16.5 14 Z" fill="currentColor" />
          <path d="M12 11.5 L12 21" stroke="currentColor" strokeWidth="1.6" opacity="0.45" />
        </svg>
      </button>

      <button
        type="button"
        aria-label="چرخش ساعتگرد"
        title="چرخش ساعتگرد"
        onClick={() => rotateBy(30)}
        onMouseEnter={hoverIn}
        onMouseLeave={hoverOut}
        style={sideBtn}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12a9 9 0 1 1 -2.6-6.3" />
          <path d="M21 4v4.5h-4.5" />
        </svg>
      </button>

      <span
        aria-hidden="true"
        style={{
          width: 1,
          height: 20,
          margin: '0 2px',
          background: 'rgba(148,163,184,0.28)',
        }}
      />

      <button
        type="button"
        aria-label={`نمای زاویه‌دار؛ شیب فعلی ${tilt} درجه`}
        title={`نمای ۴۵/۶۷ درجه برای دیدن نمای ساختمان‌ها (مثل Google Earth) · شیب فعلی ${tilt}° · میانبر: Ctrl را نگه دارید و اسکرول کنید`}
        onClick={cycleTilt}
        style={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          width: 46,
          height: 30,
          padding: 0,
          color: tilt > 0 ? '#5eead4' : '#cbd5e1',
          background: tilt > 0 ? 'rgba(45,212,191,0.10)' : 'transparent',
          border: `1px solid ${tilt > 0 ? 'rgba(45,212,191,0.28)' : 'transparent'}`,
          borderRadius: 7,
          cursor: 'pointer',
          transition: 'background 140ms ease, color 140ms ease',
        }}
        onMouseEnter={(e) => {
          if (tilt > 0) { e.currentTarget.style.background = 'rgba(45,212,191,0.18)'; }
          else { e.currentTarget.style.background = 'rgba(51,65,85,0.7)'; e.currentTarget.style.color = '#f1f5f9'; }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = tilt > 0 ? 'rgba(45,212,191,0.10)' : 'transparent';
          e.currentTarget.style.color = tilt > 0 ? '#5eead4' : '#cbd5e1';
        }}
      >
        <svg width="16" height="14" viewBox="0 0 24 20" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 13 L12 4 L21 13 L12 18 Z" />
          <path d="M12 4 L12 18" opacity="0.4" />
        </svg>
        <span style={{ fontFamily: "'Vazirmatn', sans-serif", fontSize: 8.5, fontWeight: 700, lineHeight: 1 }}>
          {tilt > 0 ? `${tilt}°` : '۳بعد'}
        </span>
      </button>
    </div>
  );
}

// Ctrl shortcut for the vector camera. Holding Ctrl suspends the map's own
// pan/zoom (gestureHandling 'none') and hands the pointer to us so a drag turns
// the view (heading) instead of moving it, and the wheel pitches the camera
// (tilt) instead of zooming - the keyboard-free twin of the rotate buttons.
// Releasing Ctrl (or losing focus) restores normal 'greedy' gestures.
//
// Self-contained hook: unlike the AnnotationMap inline version it tracks no
// drawing-mode or ctrl ref, so it drops straight into any vector Map.
export function useCtrlCamera(map) {
  useEffect(() => {
    if (!map) return undefined;
    const div = map.getDiv();
    let active = false; // Ctrl currently held
    let dragging = false; // a rotate-drag is in progress
    let startX = 0;
    let startHeading = 0;
    // Coalesce camera updates onto a single animation frame. Mousemove + wheel
    // fire faster than the vector renderer paints, so writing setHeading on
    // every event produces visible stutter. moveCamera batches the fields.
    let pendingHeading = null;
    let pendingTilt = null;
    let rafId = 0;
    const flush = () => {
      rafId = 0;
      const cam = {};
      if (pendingHeading != null) { cam.heading = pendingHeading; pendingHeading = null; }
      if (pendingTilt != null) { cam.tilt = pendingTilt; pendingTilt = null; }
      if (cam.heading != null || cam.tilt != null) map.moveCamera(cam);
    };
    const schedule = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(flush);
    };

    const setCursor = (c) => map.setOptions({ draggableCursor: c });

    const enter = () => {
      if (active) return;
      active = true;
      map.setOptions({ gestureHandling: 'none' });
      setCursor('ew-resize');
    };
    const exit = () => {
      if (!active) return;
      active = false;
      dragging = false;
      map.setOptions({ gestureHandling: 'greedy' });
      setCursor(null);
      if (rafId) { cancelAnimationFrame(rafId); rafId = 0; }
      pendingHeading = null;
      pendingTilt = null;
    };

    const onKeyDown = (e) => { if (e.key === 'Control' || e.ctrlKey) enter(); };
    const onKeyUp = (e) => { if (e.key === 'Control' || !e.ctrlKey) exit(); };

    const onMouseDown = (e) => {
      if (!active) return;
      dragging = true;
      startX = e.clientX;
      startHeading = map.getHeading?.() || 0;
      setCursor('grabbing');
      e.preventDefault();
      e.stopPropagation();
    };
    const onMouseMove = (e) => {
      if (!active || !dragging) return;
      // ~0.35 deg/px: a comfortable ~1000 px swipe = one full revolution
      // without overshoot on small drags. Coalesced onto the next frame.
      const dx = e.clientX - startX;
      pendingHeading = (((startHeading + dx * 0.35) % 360) + 360) % 360;
      schedule();
    };
    const onMouseUp = () => {
      if (!dragging) return;
      dragging = false;
      if (active) setCursor('ew-resize');
    };

    const onWheel = (e) => {
      if (!active) return;
      e.preventDefault();
      e.stopPropagation();
      // Proportional to deltaY so mouse-wheel notches (~100/notch -> ~5 deg)
      // feel responsive while trackpad two-finger scroll (small deltas) glides
      // smoothly. Clamped to the Maps vector tilt range [0, 67.5].
      const cur = pendingTilt != null ? pendingTilt : (map.getTilt?.() || 0);
      pendingTilt = Math.max(0, Math.min(67.5, cur - e.deltaY * 0.05));
      schedule();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', exit);
    window.addEventListener('mousemove', onMouseMove, true);
    window.addEventListener('mouseup', onMouseUp, true);
    if (div) {
      div.addEventListener('mousedown', onMouseDown, true);
      div.addEventListener('wheel', onWheel, { capture: true, passive: false });
    }

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', exit);
      window.removeEventListener('mousemove', onMouseMove, true);
      window.removeEventListener('mouseup', onMouseUp, true);
      if (div) {
        div.removeEventListener('mousedown', onMouseDown, true);
        div.removeEventListener('wheel', onWheel, true);
      }
      if (rafId) cancelAnimationFrame(rafId);
      if (active) map.setOptions({ gestureHandling: 'greedy' });
    };
  }, [map]);
}

// Bottom-right help cluster (sits above the rotate control): a keyboard-shortcuts
// button and a symbology/KMZ-legend button. Same dark-glass language as the other
// instruments so the whole bottom-right corner reads as one control surface.
export function MapHelpCluster({ onShortcuts, onLegend }) {
  const btn = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    height: 30, padding: '0 11px', background: 'transparent', color: '#cbd5e1',
    border: '1px solid transparent', borderRadius: 7, cursor: 'pointer',
    fontFamily: "'Vazirmatn', sans-serif", fontSize: 12, fontWeight: 600,
    transition: 'background 140ms ease, color 140ms ease',
  };
  const hoverIn = (e) => { e.currentTarget.style.background = 'rgba(51,65,85,0.7)'; e.currentTarget.style.color = '#f1f5f9'; };
  const hoverOut = (e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#cbd5e1'; };
  return (
    <div style={glassCard(106)}>
      <button type="button" onClick={onLegend} title="راهنمای نمادها و رنگ‌ها (KMZ)"
              aria-label="راهنمای نمادها" style={btn} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9.5" /><path d="M12 16v-4" /><path d="M12 8h.01" />
        </svg>
        نمادها
      </button>
      <span aria-hidden="true" style={{ width: 1, height: 18, background: 'rgba(148,163,184,0.28)' }} />
      <button type="button" onClick={onShortcuts} title="میانبرهای صفحه کلید"
              aria-label="میانبرهای صفحه کلید" style={btn} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
        <svg width="16" height="14" viewBox="0 0 24 20" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="2" y="3" width="20" height="14" rx="2.5" /><path d="M6 8h.01M10 8h.01M14 8h.01M18 8h.01M7 12h10" />
        </svg>
        میانبرها
      </button>
    </div>
  );
}

// Simplified satellite / roadmap segmented toggle. Same dark-glass language as
// MapRotateControl, anchored just below it. Controlled component: the caller
// owns `value` ('satellite' | 'roadmap') and applies it imperatively via
// map.setMapTypeId in its own effect.
export function MapTypeToggle({ value, onChange }) {
  const opts = [
    { id: 'satellite', label: 'ماهواره‌ای' },
    { id: 'roadmap', label: 'نقشه' },
  ];
  return (
    <div style={glassCard(14)}>
      {opts.map((o) => {
        const active = value === o.id;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            aria-pressed={active}
            style={{
              padding: '7px 15px',
              background: active
                ? 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)'
                : 'transparent',
              color: active ? '#f0fdfa' : '#cbd5e1',
              fontFamily: "'Vazirmatn', sans-serif",
              fontSize: 12.5,
              fontWeight: active ? 700 : 600,
              lineHeight: 1,
              border: active
                ? '1px solid rgba(45,212,191,0.4)'
                : '1px solid transparent',
              borderRadius: 7,
              cursor: 'pointer',
              transition: 'background 140ms ease, color 140ms ease',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
